import { Button, Divider } from 'antd';
import React, { useCallback, useMemo } from 'react';
import { PricingTreeItem } from '../../shared/logik/hooks/pricing.hooks';
import { formatPriceValue } from '../../shared/utils/numbers.utils';
import { CloseOutlined } from '@ant-design/icons';
import { useAppDispatch } from '../../app/hooks';
import { removeWallItem } from './thunks/removeWallItem';
import { useFieldValue } from '../../shared/logik/hooks/field.hooks';
import { LogikFieldsEnum } from '../../shared/logik/constants/fields.constant';

export const renderPricingRows = (items: PricingTreeItem[], props?: PricingRowProps): JSX.Element[] =>
    items
        .filter((e) => !!e.data)
        .sort((a, b) => {
            if (!a.data || !b.data) return 0;
            if (a.data.description && b.data.description) {
                const reg = /^(?<num>\d+):/;

                const aMatch = reg.exec(a.data.description)?.groups?.['num'];
                const bMatch = reg.exec(b.data.description)?.groups?.['num'];

                if (aMatch && bMatch) return Number(aMatch) - Number(bMatch);
            }

            return (
                (a.data.orderNumber ?? 0) -
                (b.data.orderNumber ?? 0) +
                a.data.uniqueIdentifier.localeCompare(b.data.uniqueIdentifier)
            );
        })
        .map((item, i) => <PricingRow key={`row-${i}`} data={item.data} products={item.products} {...props} />);

type PricingRowProps = {
    hideDivider?: boolean;
    className?: string;
    showRemoveButton?: boolean;
};

const PricingRowImpl = ({ data, products, ...props }: PricingRowProps & PricingTreeItem): JSX.Element => {
    const viewPrice = useFieldValue<boolean>(LogikFieldsEnum.viewPrice);
    const dispatch = useAppDispatch();
    const hasChildren = products.length > 0;
    const isWallItemRow = useMemo(() => {
        return data?.uniqueIdentifier.startsWith('WallItem-');
    }, [data]);

    const onDeleteRow = useCallback(async () => {
        if (!data || !props.showRemoveButton) return;
        const confirmed = confirm(
            `Are you sure you want to remove "${data.description}"? This action cannot be undone.`,
        );

        if (confirmed) {
            console.debug('data', { data, products, props });
            const wallItemUniqueId = data.uniqueIdentifier.match(/WallItem-(?<id>\d+)/)?.groups?.id;
            if (wallItemUniqueId) await dispatch(removeWallItem({ wallItemUniqueId }));
        }
    }, [data, dispatch, products, props]);

    return data ? (
        <>
            <div className={'pricing-row' + (data.extended?.Highlight ? ' pricing-row-bold' : '')}>
                <div className='pricing-row-label'>
                    {props.showRemoveButton && isWallItemRow && (
                        <Button
                            icon={<CloseOutlined />}
                            onClick={onDeleteRow}
                            type={'link'}
                            size={'small'}
                            danger
                            className={'row-remove-btn'}
                        />
                    )}{' '}
                    {data.description}
                    {hasChildren && ':'}
                </div>
                {!data.extended?.HidePrice && (data.level !== 0 ? viewPrice : viewPrice || data.orderNumber != 1) && (
                    <>
                        <div className={'pricing-row-value'}>
                            {data.extended?.SectionPricingDet === 'Footer'
                                ? data.extPrice || data.extended?.extPrice
                                : formatPriceValue(data.extPrice || data.extended?.extPrice, true, {
                                      minimumFractionDigits: 2,
                                  })}
                        </div>
                    </>
                )}
            </div>

            {hasChildren && (
                <div className={data.level !== 0 ? 'pricing-row-children' : ''}>
                    {renderPricingRows(products, props)}
                    {data.level === 0 && props.hideDivider !== true && <Divider className='pricing-row-divider' />}
                </div>
            )}
        </>
    ) : (
        <></>
    );
};

export type PricingRowsProps = PricingRowProps & {
    items: PricingTreeItem[];
};
const PricingRowsImpl = ({ items, ...props }: PricingRowsProps): JSX.Element => {
    return <>{renderPricingRows(items, props)}</>;
};
const PricingRow = React.memo(PricingRowImpl);
export const PricingRows = React.memo(PricingRowsImpl);
